import { WorkSheetProductStatusModelType } from '@teinor/erp/types/company/order/sell/workSheet/workSheetProductStatus'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { ModalCreateEditLittleLoading, LittleModalCreateEdit } from '../../../../../../../../../baseComponents/ModalsLayouts/ModalCreateEdit'
import { ModalEditComponentProps } from '../../../../../../../../../baseComponents/ModalsLayouts/types'
import { permissionCheck } from '../../../../../../../../../baseComponents/PermissionChecker/function'
import { RowData } from '../../../../../../../../../baseComponents/TwinTable/types'
import { FormRender } from '../../../../../../../../../forms/FormRenderer/types'
import { customerProjectTypeColors } from '../../../../../../../../../utils/globals/colors'
import { twinFetchPostJSON } from '../../../../../../../../../utils/globals/data'
import { valueOrDefaultValue } from '../../../../../../../../../utils/globals/valueOrDefaultValue'
import useWorkSheetProductStatusType from '../../../../../../../../../utils/hooks/sells/orders/useWorkSheetProductStatus'
import useTwinTranslation from '../../../../../../../../../utils/hooks/useTwinTranslation'
import { useParams } from 'react-router-dom'
import { connect, ConnectedProps } from 'react-redux'
import withLoading from '../../../../../../../../../utils/hoc/withLoading'
import { AllReduxPayloads } from '../../../../../../../../../utils/reducers'
import { addDictionaryExtraLine } from '../../../../../../../../../utils/globals/dictionary'
import { emptyExtraLineFirstSortFunction } from '../../../../../../../../../forms/CustomSelect/functions'
import { LabelsPayload } from '../../../../../../../../../utils/reducers/company/labels'
import { SpecificPriceRulesPayload } from '../../../../../../../../../utils/reducers/products/specificPriceRules'

type ModalCEWorkSheetProductStatusProps = ModalEditComponentProps & ReduxModalCEWorkSheetProductStatus & { }

const ModalCEWorkSheetProductStatus: React.FC<ModalCEWorkSheetProductStatusProps> = ({ userPermissions, allRowData, ...rest }) => {
    const { t } = useTwinTranslation()
    const params = useParams<{ WorkSheetGroupStatusId?: string }>()
    const { colorRandom, colors } = useModalCEWorkSheetProductStatusLogic({ allRowData })
    const { workSheetProductStatus } = useWorkSheetProductStatusType()
    const dictLabels = useMemo(() => addDictionaryExtraLine(JSON.parse(JSON.stringify(rest.labels|| {}))), [rest.labels])

    const fields: FormRender<WorkSheetProductStatusModelType> = [
        {
            cols: 1,
            elements: [
                {
                    name: 'name',
                    label: t('name', 'Nombre'),
                    component: 'InputWithLabelMargin',
                    type: 'text',
                    required: true,
                },
                {
                    name: 'color',
                    label: t('color', 'Color'),
                    component: 'InputColorWithMargin',
                    colors: colors,
                    defaultValue: allRowData?.color || colorRandom,
                },
                {
                    name: 'typeStatus',
                    label: t('status', 'Estado'),
                    component: 'CustomSelect',
                    required: true,
                    items: workSheetProductStatus,
                },
                {
                    name: 'LabelId',
                    label: t('tag', 'Etiqueta'),
                    component: 'CustomSelectWithSearchBar',
                    items: dictLabels,
                    sortFunction: emptyExtraLineFirstSortFunction
                },
                {
                    name: 'SpecificPriceRuleForLabelId',
                    label: t('specificPriceRule', 'Tarifa'),
                    component: 'CustomSelectWithSearchBar',
                    items: rest.specificPriceRules?.dictionary || {},
                },
                {
                    name: 'id',
                    component: 'InputHidden',
                    value: allRowData?.id
                },
                {
                    name: 'order',
                    component: 'InputHidden',
                    value: allRowData?.order 
                },
                {
                    name: 'WorkSheetGroupStatusId',
                    component: 'InputHidden',
                    value: params?.WorkSheetGroupStatusId
                },
            ]
        }
    ]
    if (allRowData && userPermissions) {
        const parsedFields = valueOrDefaultValue(fields, userPermissions, allRowData)
        return (
            <ModalCreateEditLittleLoading loading={false} fields={parsedFields} url={'/api/app/order/sell/workSheet/workSheetProductStatus/updateWorkSheetProductStatus'} translations={{ title: t('editWorkSheetProductStatus', 'Editar estado de hoja de trabajo'), button: t('save', 'Guardar') }} haveButtonPermissions={permissionCheck(userPermissions, 'update')} {...rest} />
        )
    }
    return (
        <LittleModalCreateEdit fields={colorRandom ? fields : []} url={'/api/app/order/sell/workSheet/workSheetProductStatus/createWorkSheetProductStatus'} translations={{
            title: t('createWorkSheetProductStatus', 'Crear estado de hoja de trabajo'),
            button: t('create', 'Crear')
        }} haveButtonPermissions={true} {...rest} />
    )
}
interface ModalCEWorkSheetProductStatusLogicProps {
    allRowData?: RowData
}
const useModalCEWorkSheetProductStatusLogic = ({ allRowData }: ModalCEWorkSheetProductStatusLogicProps) => {
    const [colorRandom, setColorRandom] = useState<null | string>(null)
    const colors = customerProjectTypeColors

    const getRandomColor = useCallback(async () => {
        let copyColors = JSON.parse(JSON.stringify(colors))
        const result = await twinFetchPostJSON('/api/app/order/sell/workSheet/workSheetProductStatus/getAllWorkSheetProductStatuses', {})
        if (result?.length) {
            for (const element of result) {
                const parsedColor = element.color.toUpperCase()
                const alreadySelectedColor = copyColors.indexOf(parsedColor)
                if (alreadySelectedColor !== -1) {
                    copyColors.splice(alreadySelectedColor, 1)
                }
            }
        }
        if (!copyColors.length) {
            copyColors = colors
        }
        const index = Math.floor(Math.random() * copyColors.length)
        setColorRandom(copyColors[index])
    }, [colors])

    useEffect(() => {
        if (!allRowData && !colorRandom) {
            getRandomColor()
        }
    }, [allRowData, colorRandom, getRandomColor])

    return { colorRandom, colors }
}

const modalCEWorkSheetProductStatusDispatch = {
    setSpecificPriceRules: (payload: SpecificPriceRulesPayload) => ({ type: 'CHANGE_SPECIFICPRICERULES', payload }),
    setLabels: (payload: LabelsPayload) => ({ type: 'CHANGE_LABELS', payload }),
}

type ReduxModalCEWorkSheetProductStatus = ConnectedProps<typeof modalCEWorksheetProductStatusConnect>
const mapModalCEWorkSheetProductStatusConnector = (state: AllReduxPayloads) => ({ specificPriceRules: state.specificPriceRules, labels: state.labels })
const modalCEWorksheetProductStatusConnect = connect(mapModalCEWorkSheetProductStatusConnector, modalCEWorkSheetProductStatusDispatch)

const modalceworksheetproductstatusConnectLoading = withLoading(ModalCEWorkSheetProductStatus, [{ fetchUrl: '/api/app/product/specificPrice/getAllSpecificPriceRules', propName: 'specificPriceRules', setFunctionName: 'setSpecificPriceRules' }, { fetchUrl: '/api/app/config/label/getAllLabels', propName: 'labels', setFunctionName: 'setLabels' }])
const ModalCEWorkSheetProductStatusConnect = modalCEWorksheetProductStatusConnect(modalceworksheetproductstatusConnectLoading)

export default ModalCEWorkSheetProductStatusConnect